import React, { useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { loadPreference, savePreference, UserPreferenceCodes } from "requests/preferences";

export const MuellerThemeContext = React.createContext(null);

export interface ThemeDefinition {
  code: string,
  isDark: boolean,
  iconName?: string,
  className: string,
  displayName: string,
  isDebugTheme?: boolean,
};

export const ThemeDefinitions: ThemeDefinition[] = [
  { code: "light", isDark: false, iconName: "light_mode", className: "", displayName: "Light" },
  { code: "dark", isDark: true, iconName: "dark_mode", className: "dark-theme", displayName: "Dark" },
  { isDebugTheme: true, code: "blue-screen", isDark: false, className: "blue-screen", displayName: "Blue (debug)" },
];

const defaultTheme = ThemeDefinitions.find(td => td.code === "light");

export interface MuellerThemeProviderValue {
  changeTheme: (newThemeCode: string) => void;
  currentTheme: ThemeDefinition;
  themeOptions: ThemeDefinition[];
};

const MuellerThemeProvider = ({ children, internetUserId }) => {
  const [currentTheme, setCurrentTheme] = useState(defaultTheme);
  const mounted = useRef(true);
  
  useEffect(() => {
    if (currentTheme.className.length) document.body.classList.add(currentTheme.className);

    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (internetUserId) {
      loadPreference(UserPreferenceCodes.muellerEnterpriseTheme, preference => {
        if (preference.value) changeTheme(preference.value);
      }, mounted);
    }
  }, [internetUserId]);


  const changeTheme = (newThemeCode: string) => {
    if (currentTheme.className.length) document.body.classList.remove(currentTheme.className);
    let newTheme = Object.values(ThemeDefinitions).find(t => t.code === newThemeCode) || defaultTheme;
    if (newTheme.className.length) document.body.classList.add(newTheme.className);
    setCurrentTheme(newTheme);
    if (!newTheme.isDebugTheme) {
      savePreference(UserPreferenceCodes.muellerEnterpriseTheme, newThemeCode, null, mounted);
    }
  };

  (window as any).changeTheme = changeTheme;

  const providerValue = {
    changeTheme,
    currentTheme,
    themeOptions: ThemeDefinitions.filter(d => !d.isDebugTheme),
  };

  return <MuellerThemeContext.Provider value={providerValue}>{children}</MuellerThemeContext.Provider>;
}

const mapStateToProps = state => ({
  internetUserId: state.user.profile.internetUserId,
});

export default connect(mapStateToProps)(MuellerThemeProvider);

/** Provides theme context to a class component. Do not use this in functional components because they can just use the context */
export const withMuellerTheme = (WrappedComponent: React.ComponentType) => {
  return (props) => {
    const themeContext = useContext(MuellerThemeContext);
    return <WrappedComponent {...props} themeContext={themeContext} />;
  };
};
